import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  PipelineOpportunity,
  OpportunityAssessment,
  OpportunityContact,
} from '@/types/pipeline';
import { PipelineActivity } from '@/hooks/usePipeline';

// Fetch single opportunity from pipeline_board view (includes all joins)
export function useOpportunityDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['opportunity-detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No opportunity ID');

      const { data, error } = await supabase
        .from('pipeline_board' as any)
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as unknown as PipelineOpportunity;
    },
    enabled: !!id,
  });
}

// Fetch activities for an opportunity (descending by date)
export function useOpportunityActivities(opportunityId: string | undefined) {
  return useQuery({
    queryKey: ['opportunity-activities', opportunityId],
    queryFn: async (): Promise<PipelineActivity[]> => {
      if (!opportunityId) return [];

      const { data, error } = await supabase
        .from('pipeline_activities' as any)
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('activity_date', { ascending: false });

      if (error) throw error;
      return data as unknown as PipelineActivity[];
    },
    enabled: !!opportunityId,
  });
}

// Fetch opportunity contacts
export function useOpportunityContacts(opportunityId: string | undefined) {
  return useQuery({
    queryKey: ['opportunity-contacts', opportunityId],
    queryFn: async (): Promise<OpportunityContact[]> => {
      if (!opportunityId) return [];

      const { data, error } = await supabase
        .from('opportunity_contacts' as any)
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('is_primary', { ascending: false })
        .order('created_at');

      if (error) throw error;
      return data as unknown as OpportunityContact[];
    },
    enabled: !!opportunityId,
  });
}

// CRUD mutations for opportunity contacts
export function useAddOpportunityContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contact: Omit<OpportunityContact, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('opportunity_contacts' as any)
        .insert([contact])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-contacts', variables.opportunity_id] });
    },
  });
}

export function useUpdateOpportunityContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; opportunity_id: string } & Record<string, unknown>) => {
      const { data, error } = await supabase
        .from('opportunity_contacts' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-contacts', variables.opportunity_id] });
    },
  });
}

export function useDeleteOpportunityContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, opportunityId }: { id: string; opportunityId: string }) => {
      const { error } = await supabase
        .from('opportunity_contacts' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
      return opportunityId;
    },
    onSuccess: (opportunityId) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-contacts', opportunityId] });
    },
  });
}

// Update opportunity assessment (strengths/risks)
export function useUpdateOpportunityAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, assessment }: { id: string; assessment: OpportunityAssessment }) => {
      const { data, error } = await supabase
        .from('pipeline_opportunities' as any)
        .update({ opportunity_assessment: assessment })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
    },
  });
}
